import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import Lightbox from "@/galleries/shared/Lightbox";
import { BlurFade } from "@/components/ui/blur-fade";
import { InViewTextEffect } from "@/components/ui/text-effect";

// Editorial-feeling masonry grid, patterned after Magnum's variable-height
// contact-sheet layouts: mixed aspect ratios, generous gutters, captions that
// surface only on interaction rather than sitting under every frame.
const ASPECTS = ["landscape", "portrait", "square"];

function aspectClass(a) {
  if (a === "portrait") return "aspect-[2/3]";
  if (a === "square") return "aspect-square";
  return "aspect-[3/2]";
}

function buildPhotos() {
  // Stand-in imagery only — picsum has no thematic control, so these are
  // labeled as placeholder plates rather than fabricated client moments.
  // Swap `src`/`full` for real client photography before shipping.
  const captions = [
    "Plate 01 — placeholder",
    "Plate 02 — placeholder",
    "Plate 03 — placeholder",
    "Plate 04 — placeholder",
    "Plate 05 — placeholder",
    "Plate 06 — placeholder",
    "Plate 07 — placeholder",
    "Plate 08 — placeholder",
    "Plate 09 — placeholder",
    "Plate 10 — placeholder",
    "Plate 11 — placeholder",
    "Plate 12 — placeholder",
  ];
  return captions.map((caption, i) => {
    const aspect = ASPECTS[i % ASPECTS.length];
    const w = 800;
    const h = aspect === "portrait" ? 1200 : aspect === "square" ? 800 : 533;
    return {
      id: i,
      caption,
      aspect,
      src: `https://picsum.photos/seed/zigler-${i}/${w}/${h}`,
      full: `https://picsum.photos/seed/zigler-${i}/1600/${Math.round((h / w) * 1600)}`,
    };
  });
}

export default function PhotoGrid() {
  const photos = useMemo(buildPhotos, []);
  const [activeIndex, setActiveIndex] = useState(null);

  const items = photos.map((p) => ({
    caption: p.caption,
    render: () => (
      <img
        src={p.full}
        alt={p.caption}
        className="max-h-[80vh] max-w-full rounded-lg object-contain"
      />
    ),
  }));

  const handleNav = (dir) => {
    setActiveIndex((i) => (i == null ? i : (i + dir + photos.length) % photos.length));
  };

  return (
    <div className="mx-auto max-w-6xl px-5 py-14 md:px-8">
      <header className="mb-10 max-w-xl">
        <InViewTextEffect
          as="p"
          preset="blur"
          className="font-mono text-xs uppercase tracking-[0.18em] text-pine/70"
        >
          Field &amp; Process
        </InViewTextEffect>
        <h2 className="mt-2 font-serif text-3xl text-ink md:text-4xl">
          Photography from the work itself
        </h2>
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em] text-rust">
          Stand-in imagery — layout demo only, swap for real client photography
        </p>
      </header>

      <div className="columns-1 gap-5 sm:columns-2 lg:columns-3">
        {photos.map((photo, i) => (
          // BlurFade (magicui.design) owns the reveal-on-scroll now — blur
          // to clear reads more photographic than the plain fade-up every
          // other gallery uses, which is the point of reserving it for
          // images specifically. Framer's hover scale stays on the button,
          // a separate node, so it never fights BlurFade's own motion.div.
          <div key={photo.id} className="mb-5 break-inside-avoid">
            <BlurFade inView delay={i * 0.08} duration={0.5} blur="10px" className="block">
              <motion.button
                type="button"
                onClick={() => setActiveIndex(i)}
                className={`group relative block w-full overflow-hidden rounded-lg bg-panel ${aspectClass(photo.aspect)}`}
                whileHover={{ scale: 1.015 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              >
                <img
                  src={photo.src}
                  alt={photo.caption}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 flex items-end bg-pine/0 p-4 opacity-0 transition-all duration-300 group-hover:bg-pine/70 group-hover:opacity-100">
                  <span className="font-serif text-lg text-white">{photo.caption}</span>
                </div>
              </motion.button>
            </BlurFade>
          </div>
        ))}
      </div>

      <Lightbox items={items} index={activeIndex} onClose={() => setActiveIndex(null)} onNav={handleNav} />
    </div>
  );
}
